import { useReducer, useEffect } from "react";
import { Category, Day, Habit, Context, State, ReportPeriod } from "../types";
import { defaultHabits, defaultContexts, defaultCategories } from "../defaultValues";
import { LocalStorageService } from "../LocalStorageService";

export type Action =
  | { type: "ADD_HABIT"; payload: Habit }
  | { type: "REMOVE_HABIT"; payload: number }
  | { type: "EDIT_HABIT"; payload: Habit }
  | { type: "TOGGLE_HABIT"; payload: { date: string; habitId: number; score: number; progress?: number } }
  | { type: "SET_SORT_ORDER"; payload: "alphabetical" | "score" }
  | { type: "LOAD_DATA"; payload: State }
  | { type: "ARCHIVE_HABIT"; payload: number }
  | { type: "TOGGLE_GREY_OUT"; payload: boolean }
  | { type: "TOGGLE_CONTEXTS"; payload: boolean }
  | { type: "TOGGLE_NOTES"; payload: boolean }
  | { type: "TOGGLE_CATEGORIES"; payload: boolean }
  | { type: "TOGGLE_HEATMAP"; payload: boolean }
  | { type: "UPDATE_DAY_NOTE"; payload: { date: string; note: string } }
  | { type: "ADD_CONTEXT"; payload: Context }
  | { type: "REMOVE_CONTEXT"; payload: number }
  | { type: "EDIT_CONTEXT"; payload: Context }
  | { type: "ARCHIVE_CONTEXT"; payload: number }
  | { type: "TOGGLE_CONTEXT"; payload: { date: string; contextId: number } }
  | { type: "ADD_CATEGORY"; payload: Category }
  | { type: "REMOVE_CATEGORY"; payload: number }
  | { type: "EDIT_CATEGORY"; payload: Category }
  | { type: "SET_REPORT_PERIOD"; payload: ReportPeriod };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_HABIT":
      return {
        ...state,
        habits: [...state.habits, action.payload],
      };
    case "REMOVE_HABIT":
      return {
        ...state,
        habits: state.habits.filter((habit) => habit.id !== action.payload),
      };
    case "EDIT_HABIT":
      return {
        ...state,
        habits: state.habits.map((habit) =>
          habit.id === action.payload.id ? action.payload : habit
        ),
      };
    case "TOGGLE_HABIT":
      const { date, habitId } = action.payload;
      const dayIndex = state.days.findIndex((day) => day.date === date);
      if (dayIndex === -1) {
        return {
          ...state,
          days: [
            ...state.days,
            {
              date,
              habits: [habitId],
              score: action.payload.score,
              progress: action.payload.progress !== undefined
                ? { [habitId]: action.payload.progress }
                : undefined
            },
          ],
        };
      } else {
        const habits = state.days[dayIndex].habits;
        const isActive = habits.includes(habitId);
        const newHabits = isActive
          ? habits.filter((id) => id !== habitId)
          : [...habits, habitId];

        let newProgress = state.days[dayIndex].progress || {};
        if (isActive) {
          if (newProgress && habitId in newProgress) {
            const { [habitId]: _, ...remainingProgress } = newProgress;
            newProgress = Object.keys(remainingProgress).length > 0
              ? remainingProgress
              : {};
          }
        } else if (action.payload.progress !== undefined) {
          newProgress = { ...newProgress, [habitId]: action.payload.progress };
        }

        const newScore = newHabits.reduce(
          (total, id) =>
            total +
            state.habits.find((habit) => habit.id === id)!.score,
          0
        );
        const newDays = state.days.map((day, index) =>
          index === dayIndex
            ? { ...day, habits: newHabits, score: newScore, progress: newProgress }
            : day
        );
        return {
          ...state,
          days: newDays,
        };
      }
    case "SET_SORT_ORDER":
      return {
        ...state,
        sortOrder: action.payload,
      };
    case "LOAD_DATA":
      return {
        ...action.payload,
        greyOutEnabled: action.payload.greyOutEnabled ?? true,
        contextsEnabled: action.payload.contextsEnabled ?? true,
        notesEnabled: action.payload.notesEnabled ?? true,
        categoriesEnabled: action.payload.categoriesEnabled ?? false,
        reportPeriod: action.payload.reportPeriod ?? "month",
      };
    case "ARCHIVE_HABIT":
      return {
        ...state,
        habits: state.habits.map((habit) =>
          habit.id === action.payload
            ? { ...habit, archived: !habit.archived }
            : habit
        ),
      };
    case "TOGGLE_GREY_OUT":
      return {
        ...state,
        greyOutEnabled: action.payload,
      };
    case "TOGGLE_CONTEXTS":
      return {
        ...state,
        contextsEnabled: action.payload,
      };
    case "TOGGLE_NOTES":
      return {
        ...state,
        notesEnabled: action.payload,
      };
    case "TOGGLE_CATEGORIES":
      return {
        ...state,
        categoriesEnabled: action.payload,
      };
    case "TOGGLE_HEATMAP":
      return {
        ...state,
        heatmapEnabled: action.payload,
      };
    case "UPDATE_DAY_NOTE":
      const noteDayIndex = state.days.findIndex(day => day.date === action.payload.date);
      if (noteDayIndex === -1) {
        return {
          ...state,
          days: [
            ...state.days,
            {
              date: action.payload.date,
              habits: [],
              score: 0,
              notes: action.payload.note
            }
          ]
        };
      }
      return {
        ...state,
        days: state.days.map(day =>
          day.date === action.payload.date
            ? { ...day, notes: action.payload.note }
            : day
        ),
      };
    case "ADD_CONTEXT":
      return {
        ...state,
        contexts: [...state.contexts, action.payload],
      };
    case "REMOVE_CONTEXT":
      return {
        ...state,
        contexts: state.contexts.filter((context) => context.id !== action.payload),
      };
    case "EDIT_CONTEXT":
      return {
        ...state,
        contexts: state.contexts.map((context) =>
          context.id === action.payload.id ? action.payload : context
        ),
      };
    case "ARCHIVE_CONTEXT":
      return {
        ...state,
        contexts: state.contexts.map((context) =>
          context.id === action.payload
            ? { ...context, archived: !context.archived }
            : context
        ),
      };
    case "TOGGLE_CONTEXT":
      const { date: contextDate, contextId } = action.payload;
      const contextDayIndex = state.days.findIndex((day) => day.date === contextDate);
      if (contextDayIndex === -1) {
        return {
          ...state,
          days: [
            ...state.days,
            {
              date: contextDate,
              habits: [],
              contexts: [contextId],
              score: 0,
            },
          ],
        };
      } else {
        const contexts = state.days[contextDayIndex].contexts || [];
        const isActive = contexts.includes(contextId);
        const newContexts = isActive
          ? contexts.filter((id) => id !== contextId)
          : [...contexts, contextId];
        const newDays = state.days.map((day, index) =>
          index === contextDayIndex
            ? { ...day, contexts: newContexts }
            : day
        );
        return {
          ...state,
          days: newDays,
        };
      }
    case "ADD_CATEGORY":
      return {
        ...state,
        categories: [...(state.categories || []), action.payload],
      };
    case "REMOVE_CATEGORY":
      return {
        ...state,
        categories: state.categories?.filter(category => category.id !== action.payload),
        habits: state.habits.map(habit =>
          habit.categoryId === action.payload
            ? { ...habit, categoryId: undefined }
            : habit
        ),
      };
    case "EDIT_CATEGORY":
      return {
        ...state,
        categories: state.categories?.map(category =>
          category.id === action.payload.id ? action.payload : category
        ),
      };
    case "SET_REPORT_PERIOD":
      return {
        ...state,
        reportPeriod: action.payload,
      };
    default:
      return state;
  }
};

const storage = new LocalStorageService();

export const useHabitState = () => {
  const [state, dispatch] = useReducer(reducer, {
    habits: defaultHabits,
    days: [] as Day[],
    contexts: defaultContexts,
    categories: defaultCategories,
    sortOrder: "alphabetical" as const,
    greyOutEnabled: true,
    contextsEnabled: true,
    notesEnabled: true,
    categoriesEnabled: false,
    heatmapEnabled: true,
    reportPeriod: "month" as ReportPeriod,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const savedData = await storage.getItem("habitTrackerData");
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          const completeData = {
            ...parsedData,
            greyOutEnabled: parsedData.greyOutEnabled ?? true,
            contextsEnabled: parsedData.contextsEnabled ?? true,
            notesEnabled: parsedData.notesEnabled ?? true,
            sortOrder: parsedData.sortOrder ?? "alphabetical",
            reportPeriod: parsedData.reportPeriod ?? "month",
            habits: Array.isArray(parsedData.habits) ? parsedData.habits : defaultHabits,
            days: Array.isArray(parsedData.days) ? parsedData.days : [],
            contexts: Array.isArray(parsedData.contexts) ? parsedData.contexts : defaultContexts,
            categories: Array.isArray(parsedData.categories) ? parsedData.categories : defaultCategories,
          };
          console.log("Loading saved data:", completeData);
          dispatch({ type: "LOAD_DATA", payload: completeData });
        } else {
          console.log("No saved data found, using defaults");
        }
      } catch (error) {
        console.error("Error loading data:", error);
        dispatch({
          type: "LOAD_DATA",
          payload: {
            habits: defaultHabits,
            days: [],
            contexts: defaultContexts,
            categories: defaultCategories,
            sortOrder: "alphabetical",
            greyOutEnabled: true,
            contextsEnabled: true,
            notesEnabled: true,
            categoriesEnabled: false,
            heatmapEnabled: true,
            reportPeriod: "month"
          }
        });
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const saveData = async () => {
      await storage.setItem("habitTrackerData", JSON.stringify(state));
    };
    saveData();
  }, [state]);

  return { state, dispatch };
};